import cn from 'classnames/bind';
import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useLocale } from '@/shared/hooks/useLocale';

import { ORDERED_CHANNEL_PROGRAM_DAYS } from '../../constants';
import { ChannelProgramDays, ChannelProgramsGroupedByResourceDay } from '../../types';

import { getDailyTitleMap } from './config';
import { getChannelProgramDayDate } from './utils';
import styles from './styles.module.css';

const cx = cn.bind(styles);

interface Props {
    programsGroupedByDate?: ChannelProgramsGroupedByResourceDay;
    firstVisibleProgramId?: number;
}

const ChannelProgramContainer = React.lazy(
    () => import('@/scenarios/legacy-player/containers/ChannelProgram/ChannelProgram')
);
const EmptyProgramComponent = React.lazy(() => import('../empty/component'));

export const ChannelProgramModal: FC<Props> = ({
    programsGroupedByDate,
    firstVisibleProgramId,
}) => {
    const intl = useIntl();
    const { lang } = useLocale();

    const days = ORDERED_CHANNEL_PROGRAM_DAYS.filter((day) => programsGroupedByDate?.[day]);

    const scrollToFirstVisibleProgram = useCallback((node: HTMLButtonElement) => {
        node?.scrollIntoView();
    }, []);

    if (days.length === 0) {
        return <EmptyProgramComponent />;
    }

    return (
        <div className={cx('program-list')}>
            {days.map((day: ChannelProgramDays, daySectionIndex) => (
                <React.Fragment key={day}>
                    <span
                        className={cx('date', {
                            first: daySectionIndex === 0,
                        })}
                    >{`${getDailyTitleMap(intl)[day]}, ${getChannelProgramDayDate(
                        day,
                        lang
                    )}`}</span>
                    <ul className={cx('program-day-list')}>
                        {programsGroupedByDate?.[day]?.map((program) => (
                            <ChannelProgramContainer
                                key={program.id}
                                program={program}
                                setElementRef={
                                    firstVisibleProgramId === program.id
                                        ? scrollToFirstVisibleProgram
                                        : null
                                }
                                className={cx('program')}
                            />
                        ))}
                    </ul>
                </React.Fragment>
            ))}
        </div>
    );
};
